import { NextRouter } from 'next/router';

import { ContentTypes } from 'src/components/thumbnail/types';

import SimpleStore from 'src/store/SimpleStore';
import { Nullable } from '../types/consumption';
import { DynamicObject } from './commonTypes';
import { ASSET_SUBTYPE } from './constants/AppConstants';
import {
  ConsumptionPageUrls,
  CountryCode,
  DisplayLanguage
} from './constants/stringConstants';
import {
  ASSET_TYPE,
  FeatureFlagConstants,
  REMOTE_CONFIG
} from './constants/utilConstants';
import { isGlobalCheck } from './globalCheckHelper';
import { getFeatureFlagConfig } from './RemoteConfig';
import { checkKidsCard, titleGenerator } from './routeHandler';

export enum PageNamesEnum {
  MOVIE_CONSUMPTION,
  TV_SHOW_CONSUMPTION,
  WEB_SERIES_CONSUMPTION
}

const isLatestRegex = /\/latest/i;
const isLatestOneRegex = /\/latest1/i;
export const patternToExcludePathname = /_next|.((js|json|map|ico|svg)$)/;

const getCorrectMovieConsumptionURL = (data: DynamicObject) => {
  const {
    genres = [],
    id = '',
    title = '',
    original_title: originalTitle = ''
  } = data;
  const isKids = checkKidsCard(genres);
  //english title needs to be used instead of transalted title during display language change
  const correctTitle = titleGenerator(originalTitle) || titleGenerator(title);
  if (!correctTitle) {
    console.error(
      '[error]: getCorrectMovieConsumptionURL original_title and title are missing'
    );
    return null;
  }
  if (isKids) {
    return `/${ConsumptionPageUrls.KIDS_CONSUMPTION_MOVIES}/${correctTitle}/${id}`;
  }
  return `/${ConsumptionPageUrls.MOVIES_CONSUMPTION}/${correctTitle}/${id}`;
};

const getShowURLPrefix = (
  assetType: number,
  assetSubType: string,
  isKids: boolean
) => {
  if (isKids) {
    return `/${ConsumptionPageUrls.KIDS_CONSUMPTION_SHOWS}`;
  }
  if (
    (assetType == ASSET_TYPE.TvShow || assetType == ASSET_TYPE.Episode) &&
    assetSubType === ASSET_SUBTYPE.ORIGINALS
  ) {
    return `/${ConsumptionPageUrls.ZEE5ORIGINALSCONSUMPTION}`;
  }
  if (
    assetType == ASSET_TYPE.TvShow &&
    assetSubType === ASSET_SUBTYPE.TV_SHOW
  ) {
    return `/${ConsumptionPageUrls.TVSHOWS_CONSUMPTION}`;
  }
  console.error('[error]: getShowURLPrefix asset type or sub type not handled');
  return null;
};

const getCorrectShowDetailsURL = (pathName: string, data: DynamicObject) => {
  const {
    genres = [],
    id: showId = '',
    title = '',
    original_title: originalTitle = '',
    asset_subtype: assetSubtype = '',
    asset_type: assetType = ''
  } = data;

  const isKids = checkKidsCard(genres);
  //english title needs to be used instead of transalted title during display language change
  const correctShowTitle =
    titleGenerator(originalTitle) || titleGenerator(title);
  if (!correctShowTitle) {
    console.error(
      '[error]: getCorrectShowDetailsURL title and originial title are missing'
    );
    return null;
  }
  const pathPrefix = getShowURLPrefix(assetType, assetSubtype, isKids);
  if (!pathPrefix) {
    return null;
  }
  return `${pathPrefix}/${correctShowTitle}/${showId}`;
};

const getCorrectEpisodeDetailsURL = (
  pathName: string,
  assetDetails: DynamicObject,
  showDetail: DynamicObject
) => {
  //english title needs to be used instead of transalted title during display language change
  const {
    original_title = '',
    id: episodeId = '',
    genres = [],
    asset_type = ''
  } = assetDetails || {};
  const pathPrefix = getCorrectShowDetailsURL(pathName, {
    genres,
    asset_type,
    ...showDetail
  });
  if (!pathPrefix) {
    return null;
  }
  const correctEpisodeTitle = titleGenerator(original_title);
  if (!correctEpisodeTitle) {
    console.error(
      '[error]: getCorrectEpoisodeDetailsURL episodeTitle not found'
    );
    return null;
  }
  const isLatest = isLatestRegex.test(pathName);
  const isLatestOne = isLatestOneRegex.test(pathName);
  if (isLatest || isLatestOne) {
    return `${pathPrefix}/latest${isLatestOne ? '1' : ''}`;
  }
  return `${pathPrefix}/${correctEpisodeTitle}/${episodeId}`;
};

export const getCorrectURL = (
  pathName: string,
  assetDetails: DynamicObject,
  showDetail?: DynamicObject
) => {
  const showDetailData = showDetail ?? assetDetails?.show_details;
  const { asset_type = '' } = assetDetails || {};
  const assetType = parseInt(asset_type);
  if (isNaN(assetType)) {
    console.error(
      `[error]: getCorrectUrl  assetType is not a number ${asset_type}`
    );
    return null;
  }

  if (assetType === ASSET_TYPE.Movie) {
    return getCorrectMovieConsumptionURL(assetDetails);
  }
  if (assetType === ASSET_TYPE.Episode) {
    return showDetailData
      ? getCorrectEpisodeDetailsURL(pathName, assetDetails, showDetailData)
      : null;
  }
  return null;
};
export const getCorrectURLServerSide = (
  pathName: string,
  response: Nullable<DynamicObject>
) => {
  if (!response || !Object.keys(response).length) {
    return null;
  }
  //check for show
  let { assetDetails } = response || {};
  let { showDetail } = response || {};
  //check for movie or video
  if (!assetDetails) {
    assetDetails = response || {};
  }
  if (!showDetail) {
    showDetail = response?.tvshow_details || {};
  }
  if (!assetDetails || !Object.keys(assetDetails)) {
    console.error(
      '[error]: getCorrectURLServerSide asset details is null or empty'
    );
    return null;
  }
  return getCorrectURL(pathName, assetDetails, showDetail);
};

/**
 * This method correct url on client side
 * @param router
 * @param pageType
 * @param singlePlayDetails
 */
export const urlAutoCorrectionOnClientSide = (
  router: NextRouter,
  pageType: ContentTypes,
  singlePlayDetails: DynamicObject
) => {
  const remoteConfig = SimpleStore.get(REMOTE_CONFIG);
  const { payload } = getFeatureFlagConfig(
    remoteConfig,
    FeatureFlagConstants.CONFIG_SEO_URL
  );
  const { useSeoUrlStructure = false } = payload || {};
  let correctURL = null;
  const seoUrlStructure = useSeoUrlStructure
    ? singlePlayDetails?.assetDetails?.extended?.seo_url_structure
    : '';
  const routerPath = router.asPath;
  if (pageType === ContentTypes.movies) {
    correctURL = getCorrectURL(
      routerPath,
      singlePlayDetails?.assetDetails,
      singlePlayDetails?.showDetails
    );
  } else if (
    pageType === ContentTypes.tvshows ||
    pageType === ContentTypes.webSeries
  ) {
    correctURL = getCorrectURL(
      routerPath,
      singlePlayDetails?.assetDetails,
      singlePlayDetails?.showDetails
    );
  } else {
    console.error('[error]: urlAutoCorrection on client side unhandled case');
    return;
  }
  correctURL = seoUrlStructure || correctURL;
  if (correctURL) {
    const isGlobal = isGlobalCheck(router.asPath);
    const contentPartnerName =
      singlePlayDetails?.assetDetails?.content_partner_details
        ?.content_partner_name;
    let finalURL = correctURL;
    if (contentPartnerName) {
      finalURL = `/add-ons/${contentPartnerName
        .toLowerCase()
        .replaceAll(' ', '-')}${finalURL}`;
    }
    finalURL = `${isGlobal ? '/global' : ''}${finalURL}`;
    if (finalURL !== router.asPath) {
      router.replace(
        {
          pathname: finalURL,
          query: {
            ...router.query // Preserve existing query parameters as it was reset to null
          }
        },
        finalURL // asPATH
      );
    }
  }
};

/**
 * Return url redirection object for getServerSideProps if required
 * Note: this function can result in infinite redirects if not validated properly.
 * @param routerLocale locale from router s
 * @param countryCode
 * @param pathName
 * @param response response from contentLite
 * @returns object to redirect if redirection is required
 */
export const URLAutoCorrectionServerSide = (
  routerLocale: string = DisplayLanguage.ENGLISH,
  countryCode: string,
  pathName: string,
  response: Nullable<DynamicObject>,
  useSeoUrlStructure = false
) => {
  const isRestrictedPathname = patternToExcludePathname.test(pathName);
  if (isRestrictedPathname) {
    return null;
  }
  const seoUrlStructure = useSeoUrlStructure
    ? response?.extended?.seo_url_structure ||
      response?.assetDetails?.extended?.seo_url_structure
    : '';
  let locale = '';
  if (routerLocale !== DisplayLanguage.ENGLISH) {
    locale = `/${routerLocale}`;
  }

  const correctURL =
    useSeoUrlStructure && seoUrlStructure
      ? encodeURI(`${locale}${seoUrlStructure}`)
      : getCorrectURLServerSide(pathName, response);
  if (!correctURL) {
    return null;
  }

  const isGlobal = countryCode !== CountryCode.IN;
  const contentPartnerName =
    response?.content_owner || response?.assetDetails?.content_owner;
  let finalURL = correctURL;
  if (contentPartnerName && pathName.includes('add-ons')) {
    finalURL = `/add-ons/${contentPartnerName
      .toLowerCase()
      .replaceAll(' ', '-')}${finalURL}`;
  }
  const utmData = pathName.split('?');
  if (utmData.length > 1) {
    finalURL = `${finalURL}?${utmData[1]}`;
  }
  finalURL = `${isGlobal ? '/global' : ''}${finalURL}`;
  if (correctURL && pathName !== finalURL) {
    return {
      redirect: {
        destination: encodeURI(`${locale}${finalURL}`),
        permanent: true
      }
    };
  }
  return null;
};
